import { useEffect } from "react";
import { useNavigate } from "react-router";
import { useDeliverySession } from "@/hooks/use-delivery-session";
import { useGuestSession } from "@/hooks/use-guest-session";
import { usePublicMenu } from "@/hooks/use-menu";
import { useTheme } from "@/hooks/use-theme";
import { useCart } from "@/lib/cart-store";
import type { PublicMenuProduct } from "@/types/menu";
import {
  createPublicMenuFlowModel,
  type PublicMenuFlowModel,
  type PublicMenuFlowVariant,
} from "./public-menu-flow-model";
import { cartScopeForFulfillment } from "./public-menu-flow-model";

export type UsePublicMenuFlowOptions =
  | {
      variant: "dine-in";
      orgSlug: string | undefined;
      qrToken: string | null;
    }
  | {
      variant: "takeaway";
      orgSlug: string | undefined;
    }
  | {
      variant: "delivery";
      orgSlug: string | undefined;
    };

export type PublicMenuFlowActions = {
  addProduct(product: PublicMenuProduct): void;
  decrementProduct(productId: string): void;
  setQuantity(productId: string, quantity: number): void;
  removeLine(productId: string): void;
  checkout(): void;
};

export type PublicMenuFlowState =
  | { status: "loading"; variant: PublicMenuFlowVariant }
  | { status: "error"; variant: PublicMenuFlowVariant; message?: string }
  | { status: "idle"; variant: PublicMenuFlowVariant }
  | {
      status: "ready";
      variant: PublicMenuFlowVariant;
      model: PublicMenuFlowModel;
      actions: PublicMenuFlowActions;
    };

export function usePublicMenuFlow(options: UsePublicMenuFlowOptions): PublicMenuFlowState {
  const navigate = useNavigate();
  const menuQuery = usePublicMenu(options.orgSlug);
  const deliverySessionQuery = useDeliverySession(
    options.variant === "delivery" ? options.orgSlug : undefined,
  );
  const deliverySession = deliverySessionQuery.data;
  const guest = useGuestSession(
    options.variant === "dine-in" ? options.orgSlug : undefined,
    options.variant === "dine-in" ? options.qrToken : null,
  );
  const cart = useCart(options.orgSlug, cartScopeForFulfillment(options.variant));
  const { setTheme, setColorTheme } = useTheme();

  const organization = menuQuery.data?.organization;
  useEffect(() => {
    if (!organization) {
      return;
    }

    const { theme, colorTheme } = organization;
    if (theme) {
      setTheme(theme as "dark" | "light" | "system");
    }
    if (colorTheme) {
      setColorTheme(colorTheme);
    }
  }, [organization, setTheme, setColorTheme]);

  const deliveryLoading =
    options.variant === "delivery" && Boolean(options.orgSlug) && deliverySessionQuery.isPending;
  if (menuQuery.isLoading || deliveryLoading) {
    return { status: "loading", variant: options.variant };
  }

  if (options.variant === "delivery" && deliverySessionQuery.isError) {
    return {
      status: "error",
      variant: options.variant,
      message:
        deliverySessionQuery.error instanceof Error
          ? deliverySessionQuery.error.message
          : undefined,
    };
  }

  if (menuQuery.error) {
    return {
      status: "error",
      variant: options.variant,
      message: menuQuery.error instanceof Error ? menuQuery.error.message : undefined,
    };
  }

  if (!menuQuery.data) {
    return options.variant === "delivery"
      ? { status: "idle", variant: options.variant }
      : { status: "error", variant: options.variant };
  }

  let model: PublicMenuFlowModel;
  if (options.variant === "delivery") {
    if (!deliverySession) {
      return { status: "idle", variant: options.variant };
    }
    model = createPublicMenuFlowModel({
      variant: options.variant,
      menu: menuQuery.data,
      cartLines: cart.state.lines,
      orgSlug: options.orgSlug,
      delivery: deliverySession,
    });
  } else if (options.variant === "takeaway") {
    model = createPublicMenuFlowModel({
      variant: options.variant,
      menu: menuQuery.data,
      cartLines: cart.state.lines,
      orgSlug: options.orgSlug,
    });
  } else {
    model = createPublicMenuFlowModel({
      variant: options.variant,
      menu: menuQuery.data,
      cartLines: cart.state.lines,
      orgSlug: options.orgSlug,
      qrToken: options.qrToken,
      tableLabel: guest.tableLabel,
      guestError: guest.error,
    });
  }

  return {
    status: "ready",
    variant: options.variant,
    model,
    actions: {
      addProduct(product) {
        cart.addLine({
          productId: product.id,
          name: product.name,
          originalPrice: product.originalPrice,
          unitPrice: product.originalPrice,
          discountedUnitPrice: product.discountedPrice,
          promotionMinQuantity: product.promotionMinQuantity,
          discountPercentage: product.discountPercentage,
          promotionName: product.promotionName,
        });
      },
      decrementProduct(productId) {
        const current = model.quantitiesByProductId.get(productId) ?? 0;
        cart.setQuantity(productId, current - 1);
      },
      setQuantity: cart.setQuantity,
      removeLine: cart.removeLine,
      checkout() {
        if (model.cartDrawer.checkoutTarget) {
          navigate(model.cartDrawer.checkoutTarget);
        }
      },
    },
  };
}
